import type { TransportItem } from '@grafana/faro-web-sdk';

import { TOFE_VERSION } from '../constants';

/** Minimal interface for the Faro API surface we actually use. */
interface FaroApi {
  pushError(error: Error): void;
  pushLog(messages: string[], options?: { level?: string; context?: Record<string, string> }): void;
  pushEvent(name: string, attributes?: Record<string, string>): void;
  setUser(user: { id: string; attributes?: Record<string, string> }): void;
  resetUser(): void;
  setSession(session: { attributes?: Record<string, string> }): void;
}

let _faro: { api: FaroApi } | undefined;

/**
 * Serializes a single log argument for Faro.
 *
 * Faro's built-in `defaultLogArgsSerializer` stringifies every argument with
 * `String(arg)`, which turns objects into the useless `[object Object]`. We
 * keep strings verbatim and JSON-stringify everything else so the second
 * argument of `console.log(msg, data)` / `logger.log(msg, data)` lands in the
 * collector as readable JSON. Falls back to `String(arg)` on circular refs.
 */
function serializeLogArg(arg: unknown): string {
  if (typeof arg === 'string') {
    return arg;
  }
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    return String(arg);
  }
}

/**
 * Faro `logArgsSerializer` that preserves object args as JSON instead of `[object Object]`.
 *
 * @internal – exported only for unit-testing purposes.
 */
export const logArgsSerializer = (args: unknown[]): string => args.map(serializeLogArg).join(' ');

/**
 * Reference to the un-patched `console.error`, used inside our own catch
 * blocks. After `patchConsoleError()` runs, `console.error` itself is wired
 * to feed errors back into Faro — so calling it from within Faro's own
 * failure path would recurse and produce a runaway loop. The default
 * indirection (resolved at call-time) keeps pre-init failures visible too.
 */
let _safeConsoleError: (...args: unknown[]) => void = (...args) => console.error(...args);

/** Limits applied to each item in the beforeSend pipeline and to the transport batch. */
export interface FaroPayloadLimits {
  /** Max stack frames kept per exception. Frames beyond this are discarded. Default: 20 */
  maxStackFrames?: number;
  /** Max characters for the error value/message field. Longer values are truncated. Default: 2048 */
  maxMessageLength?: number;
  /** Drop the whole event when its serialised payload exceeds this size (KB). Default: 256 */
  maxPayloadSizeKb?: number;
  /** Max items flushed in a single batch request. Default: 50 */
  maxBatchItems?: number;
}

const DEFAULT_PAYLOAD_LIMITS: Required<FaroPayloadLimits> = {
  maxStackFrames: 20,
  maxMessageLength: 2048,
  maxPayloadSizeKb: 256,
  maxBatchItems: 50,
};

/**
 * Selective HTTP payload capture for Faro logs.
 *
 * Opt-in by URL regex: when `enabled` is true and `includeUrls` is non-empty, the
 * `FaroHttpLoggerInterceptor` pushes serialized request/response bodies to Faro as
 * structured log entries. Bodies are run through the redact utility before serialization.
 */
export interface FaroHttpCaptureConfig {
  enabled: boolean;
  /** Regex sources matched against the request URL. Empty list means nothing is logged. */
  includeUrls?: string[];
  /** Regex sources that win over `includeUrls`. */
  excludeUrls?: string[];
  captureRequestBody?: boolean;
  captureResponseBody?: boolean;
  captureRequestHeaders?: boolean;
  /** Field names redacted in serialized bodies (case-insensitive). */
  redactKeys?: string[];
  /** Truncate the serialized body at this many characters. */
  maxBodyChars?: number;
  /** Skip the body entirely when serialized size exceeds this many KB. */
  skipBodyOverKb?: number;
}

/**
 * Tuning for the Faro collector transport.
 *
 * Critical when the collector is served same-origin (e.g. `/faro/collect` behind
 * the app's reverse proxy): Faro telemetry then competes for the browser's
 * per-host connection pool (~6 sockets in HTTP/1.1) with the app's own API and
 * resource requests. Faro's default transport allows up to 5 concurrent sends,
 * so a slow/hanging collector can pin almost the entire pool and stall critical
 * requests (e.g. the Veridas document step). Keep `concurrency` low so telemetry
 * never starves the app.
 */
export interface FaroTransportConfig {
  /** Max concurrent collector requests. Default: 2 (Faro's own default is 5). */
  concurrency?: number;
  /** Max queued batches before the oldest are dropped. Default: 30. */
  bufferSize?: number;
}

const DEFAULT_TRANSPORT_CONFIG: Required<FaroTransportConfig> = {
  concurrency: 2,
  bufferSize: 30,
};

export interface FaroInitConfig {
  enabled: boolean;
  url: string;
  apiKey?: string;
  appName?: string;
  appVersion?: string;
  environment?: string;
  sessionSampleRate?: number;
  captureConsole?: boolean;
  virtualhost?: string;
  payloadLimits?: FaroPayloadLimits;
  httpCapture?: FaroHttpCaptureConfig;
  transport?: FaroTransportConfig;
}

let faroInitialized = false;
let _sessionAttributes: Record<string, string> = {};

/**
 * Returns a `beforeSend` callback that enforces the given payload limits.
 *
 * Processing order for each item:
 *  1. If the item is an exception: truncate `value` and `stacktrace.frames`.
 *  2. Measure the serialised payload size and drop the item when it exceeds the KB limit.
 *
 * @internal – exported only for unit-testing purposes.
 */
export function createBeforeSend(limits: Required<FaroPayloadLimits>): (item: TransportItem) => TransportItem | null {
  return (item: TransportItem): TransportItem | null => {
    let processedItem = item;

    if (item.type === 'exception') {
      // Cast: when type === Exception the payload always matches ExceptionEvent shape.
      const payload = item.payload as { value?: string; stacktrace?: { frames?: unknown[] } };
      let modified = false;

      const patchedPayload: typeof payload = { ...payload };

      if (typeof patchedPayload.value === 'string' && patchedPayload.value.length > limits.maxMessageLength) {
        patchedPayload.value = patchedPayload.value.slice(0, limits.maxMessageLength) + '\u2026'; // …
        modified = true;
      }

      if (
        Array.isArray(patchedPayload.stacktrace?.frames) &&
        patchedPayload.stacktrace.frames.length > limits.maxStackFrames
      ) {
        patchedPayload.stacktrace = {
          ...patchedPayload.stacktrace,
          frames: patchedPayload.stacktrace.frames.slice(0, limits.maxStackFrames),
        };
        modified = true;
      }

      if (modified) {
        processedItem = { ...item, payload: patchedPayload as unknown as typeof item.payload };
      }
    }

    const serialised = JSON.stringify(processedItem.payload);
    const sizeKb = new Blob([serialised]).size / 1024;

    if (sizeKb > limits.maxPayloadSizeKb) {
      // console.warn is not patched, safe to call directly.
      console.warn(
        `[Faro] Dropping ${processedItem.type} event: payload ${sizeKb.toFixed(1)} KB exceeds limit of ${limits.maxPayloadSizeKb} KB`,
      );
      return null;
    }

    return processedItem;
  };
}

/**
 * Resets Faro initialization state - FOR TESTING ONLY
 * @internal
 */
export function resetFaroForTesting(): void {
  faroInitialized = false;
  _faro = undefined;
  _sessionAttributes = {};
  // Restore the indirection so the next test's freshly installed
  // console.error spy is resolved at call-time.
  _safeConsoleError = (...args) => console.error(...args);
}

/**
 * Initializes the Grafana Faro Web SDK for Real User Monitoring (RUM)
 *
 * @param config - Configuration object for Faro initialization
 * @returns Promise that resolves when Faro is initialized
 */
export async function initFaro(config: FaroInitConfig): Promise<void> {
  if (!config.enabled) {
    console.debug('[Faro] Observability disabled by configuration');
    return;
  }

  if (faroInitialized) {
    console.warn('[Faro] Already initialized, skipping re-initialization');
    return;
  }

  const virtualhost = config.virtualhost ?? (typeof window !== 'undefined' ? window.location.hostname : 'unknown');

  const resolvedLimits: Required<FaroPayloadLimits> = {
    ...DEFAULT_PAYLOAD_LIMITS,
    ...config.payloadLimits,
  };

  try {
    const [
      { initializeFaro, getWebInstrumentations, LogLevel, InternalLoggerLevel, FetchTransport },
      { TracingInstrumentation },
    ] = await Promise.all([import('@grafana/faro-web-sdk'), import('@grafana/faro-web-tracing')]);

    // Explicit transport with a low concurrency cap so collector traffic cannot
    // monopolise the browser's per-host connection pool (see FaroTransportConfig).
    // When `transports` is set, `url`/`apiKey` must NOT also be passed top-level.
    const transport = new FetchTransport({
      url: config.url,
      apiKey: config.apiKey,
      concurrency: config.transport?.concurrency ?? DEFAULT_TRANSPORT_CONFIG.concurrency,
      bufferSize: config.transport?.bufferSize ?? DEFAULT_TRANSPORT_CONFIG.bufferSize,
    });

    const instance = initializeFaro({
      transports: [transport],
      app: {
        name: config.appName ?? 'tofev2',
        version: config.appVersion ?? '0.0.0',
        environment: config.environment || 'production',
        virtualhost,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
      sessionTracking: {
        enabled: true,
        persistent: false,
        samplingRate: config.sessionSampleRate ?? 1.0,
      },
      batching: {
        enabled: true,
        itemLimit: resolvedLimits.maxBatchItems,
        sendTimeout: 2000,
      },
      instrumentations: [
        ...getWebInstrumentations({
          captureConsole: config.captureConsole ?? false,
        }),
        new TracingInstrumentation(),
      ],
      consoleInstrumentation: {
        // DEBUG and TRACE are too verbose; ERROR is handled by patchConsoleError()
        // which preserves the original stack trace. Faro's built-in console.error
        // capture is disabled here to avoid duplicates with broken stack traces.
        disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE, LogLevel.ERROR],
        consoleErrorAsLog: false,
      },
      internalLoggerLevel: InternalLoggerLevel.ERROR,
      logArgsSerializer,
      beforeSend: createBeforeSend(resolvedLimits),
    });

    _faro = instance;
    faroInitialized = true;
    patchConsoleError();
    console.debug('[Faro] Successfully initialized with virtualhost:', virtualhost);
  } catch (error) {
    _safeConsoleError('[Faro] Failed to initialize:', error);
    // Don't throw - we don't want observability issues to break the app
  }
}

/** Tracks Error instances already pushed to Faro to avoid duplicates. */
const _pushedErrors = new WeakSet<Error>();

/**
 * Gets the Faro instance
 * @returns Faro instance or undefined if not initialized
 */
export function getFaro() {
  return faroInitialized ? _faro : undefined;
}

/**
 * Pushes an error to Faro, deduplicating Error instances to avoid double-reporting
 * (e.g. when both FaroLoggerMonitor and patchConsoleError handle the same error).
 */
export function pushErrorToFaro(error: Error | string): void {
  const faro = getFaro();
  if (!faro) {
    return;
  }

  try {
    if (error instanceof Error) {
      if (_pushedErrors.has(error)) return;
      _pushedErrors.add(error);
      faro.api.pushError(error);
    } else {
      faro.api.pushError(new Error(error));
    }
  } catch (e) {
    _safeConsoleError('[Faro] Failed to push error:', e);
  }
}

/**
 * Patches console.error to forward errors to Faro with the original stack trace.
 * Faro's built-in console instrumentation loses the stack of Error objects passed
 * as arguments; this patch extracts the first Error instance and pushes it directly
 * via faro.api.pushError(), falling back to a synthetic Error for plain strings.
 * Called once after initializeFaro() succeeds.
 */
function patchConsoleError(): void {
  const original = console.error.bind(console);
  // Pin the un-patched reference so Faro's own catch blocks can log without
  // re-entering this handler (which would otherwise create a recursive loop:
  // pushError throws → console.error → pushError → throws → ...).
  _safeConsoleError = original;
  let inFlight = false;
  console.error = (...args: unknown[]) => {
    original(...args);
    // Defense-in-depth: if anything we call below ends up invoking
    // console.error synchronously (directly or via a third-party patch), bail
    // out instead of recursing.
    if (inFlight) return;
    inFlight = true;
    try {
      const err = args.find((a): a is Error => a instanceof Error);
      if (err) {
        pushErrorToFaro(err);
      } else {
        const message = args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
        pushErrorToFaro(new Error(message));
      }
    } finally {
      inFlight = false;
    }
  };
}

/**
 * Pushes a log message to Faro
 * @param message - Log message
 * @param level - Log level
 * @param context - Additional context (must have string values)
 */
export function pushLogToFaro(message: string, level = 'info', context?: Record<string, string>): void {
  const faro = getFaro();
  if (!faro) {
    return;
  }

  try {
    faro.api.pushLog([message], {
      level,
      context,
    });
  } catch (error) {
    _safeConsoleError('[Faro] Failed to push log:', error);
  }
}

/**
 * Pushes a custom event to Faro
 * @param name - Event name
 * @param attributes - Event attributes (must have string values)
 */
export function pushEventToFaro(name: string, attributes?: Record<string, string>): void {
  const faro = getFaro();
  if (!faro) {
    return;
  }

  try {
    faro.api.pushEvent(name, attributes);
  } catch (error) {
    _safeConsoleError('[Faro] Failed to push event:', error);
  }
}

/**
 * Sets user information in Faro
 * @param id - User ID
 * @param attributes - Additional user attributes
 */
export function setFaroUser(id: string, attributes?: Record<string, string>): void {
  const faro = getFaro();
  if (!faro) {
    return;
  }

  try {
    faro.api.setUser({
      id,
      attributes,
    });
  } catch (error) {
    _safeConsoleError('[Faro] Failed to set user:', error);
  }
}

/**
 * Resets user information in Faro
 */
export function resetFaroUser(): void {
  const faro = getFaro();
  if (!faro) {
    return;
  }

  try {
    faro.api.resetUser();
  } catch (error) {
    _safeConsoleError('[Faro] Failed to reset user:', error);
  }
}

/**
 * Sets custom session attributes in Faro
 * @param attributes - Session attributes (must have string values)
 */
export function setFaroSessionAttributes(attributes: Record<string, string>): void {
  const faro = getFaro();
  if (!faro) {
    return;
  }

  try {
    _sessionAttributes = {
      ..._sessionAttributes,
      ...attributes,
    };
    faro.api.setSession({ attributes: _sessionAttributes });
  } catch (error) {
    _safeConsoleError('[Faro] Failed to set session attributes:', error);
  }
}

/**
 * Initializes Faro from app config, applying fallback values for optional fields.
 * Intended to be called directly before bootstrapApplication (no Angular DI needed).
 *
 * @param faroConfig - Faro config from app config, or undefined if not configured
 * @param fallback - Fallback values for appVersion and environment
 */
export async function initFaroFromConfig(
  faroConfig: FaroInitConfig | undefined,
  fallback: { environment?: string; appVersion?: string } = {},
): Promise<void> {
  if (!faroConfig) {
    console.debug('[Faro] No configuration found');
    return;
  }
  await initFaro({
    ...faroConfig,
    appName: faroConfig.appName ?? 'tofev2',
    appVersion: faroConfig.appVersion ?? fallback.appVersion ?? TOFE_VERSION,
    environment: faroConfig.environment ?? fallback.environment,
  });
}
